import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import ConfirmPopup from "../popover/confirmPopup";


const UpdateFile = forwardRef((props, ref) => {
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    
    // Expose reset method to parent component
    useImperativeHandle(ref, () => ({
        reset: () => {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }));
    
    const handleOnClose = () => {
        setError(null);
    };
    
    const validateFileType = (file, acceptTypes) => {
        // If no accept types are specified, use default
        const acceptedTypes = acceptTypes || 'image/*,application/pdf';
        
        const validTypes = acceptedTypes.split(',').map(type => type.trim());
        
        // Check if the file type matches any of the valid types
        const isValid = validTypes.some(type => {
            // Handle wildcard mime types like "image/*"
            if (type.includes('/*')) {
                const mainType = type.split('/')[0];
                return file.type.startsWith(mainType);
            }
            // Handle specific mime types
            else if (type.includes('/')) {
                return file.type === type;
            }
            // Handle file extensions like ".pdf"
            else if (type.startsWith('.')) {
                return file.name.toLowerCase().endsWith(type.toLowerCase());
            }
            return false;
        });
        
        return isValid;
    };
    
    const isImageFile = (file) => {
        return file.type.startsWith('image/');
    };
    
    const onFileChange = (e) => {
        setError(null);
        
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        
        let file = e.target.files[0];
        
        // Validate file type against accept prop
        if (!validateFileType(file, props.accept)) {
            setError(`Invalid file type. Accepted formats are: ${props.accept || 'image/*,application/pdf'}`);
            e.target.value = ''; // Clear the input
            return;
        }
        
        // Handle different file types appropriately
        if (isImageFile(file)) {
            // For images, use base64 encoding
            let reader = new FileReader();
            
            reader.onerror = () => {
                setError('Error reading file.');
                e.target.value = ''; // Clear the input
            };

            reader.onload = () => {
                let fileInfo = {
                    name: file.name,
                    type: file.type,
                    size: Math.round(file.size / 1000) + ' kB',
                    base64: reader.result,
                    file: file,
                    format: 'base64'
                };
                props.onDone(fileInfo);
            };
            
            reader.readAsDataURL(file);
        } else {
            // For HTML, PDF, and other text-based files, read as text
            let reader = new FileReader();
            
            reader.onerror = () => {
                setError('Error reading file.');
                e.target.value = ''; // Clear the input
            };

            reader.onload = () => {
                let fileInfo = {
                    name: file.name,
                    type: file.type,
                    size: Math.round(file.size / 1000) + ' kB',
                    content: reader.result,
                    file: file,
                    format: 'text'
                };
                props.onDone(fileInfo);
            };
            
            // Read as text for HTML files
            if (file.type === 'text/html' || file.name.toLowerCase().endsWith('.html')) {
                reader.readAsText(file);
            } 
            // For PDFs, still use DataURL as they're binary
            else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
                reader.onload = () => {
                    let fileInfo = {
                        name: file.name,
                        type: file.type,
                        size: Math.round(file.size / 1000) + ' kB',
                        content: reader.result,
                        file: file,
                        format: 'pdf'
                    };
                    props.onDone(fileInfo);
                };
                reader.readAsDataURL(file);
            }
            // Default to text for other formats
            else {
                reader.readAsText(file);
            }
        }
    };

    return (
        <>
            <label htmlFor={props.inputId}>{props.submitText ? props.submitText : 'Upload File'}</label>
            <input
                ref={fileInputRef}
                type="file"
                disabled={props.disabled? true : false}
                onChange={e => onFileChange(e)}
                accept={props.accept ? props.accept : 'image/*,application/pdf'}
                id={props.inputId} 
            />
            {error && (
                <ConfirmPopup
                    details={{
                    icon: '',
                    type: 'warning',
                    title: 'Invalid File Type',
                    description: error,
                    buttons: 'O',
                    }}
                    onCloseClick={handleOnClose} 
            />)}
        </>
    )
});

export default UpdateFile;